import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import styles from "./LayoutHeader.module.scss";

import { CompanyContext } from '@/app/providers/ParamsProvider/ParamsProvider';
import { showToast } from "../../../utils/GlobalUtil";
import { changePeriod } from '../api/changePeriod';
import { MESSAGES } from '../constants';
import { AllPageData, CompanyContextType, CompanyData, PeriodData } from '../types';

// 페이지 상수들 import
import ACCOUNTING_PAGE_DATA from '@/app/constants/pages/accounting';
import COMMON_PAGE_DATA from '@/app/constants/pages/common';
import COPERATE_PAGE_DATA from '@/app/constants/pages/coperate';
import PERSONAL_PAGE_DATA from '@/app/constants/pages/personal';
import SALARY_INSURANCE_PAGE_DATA from '@/app/constants/pages/salaryinsurance';
import VAT_PAGE_DATA from '@/app/constants/pages/vat';

export const LayoutHeader = () => {
    const companyInfo = useContext(CompanyContext) as CompanyContextType | null;
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const activeItemRef = useRef<HTMLLIElement>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodData | null>(null);

    const companyData: CompanyData | undefined = companyInfo?.company;
    const periodData: PeriodData | undefined = companyInfo?.accountingPeriod;
    const periodList: PeriodData[] | undefined = companyInfo?.periodList;

    // 모든 페이지 데이터를 통합
    const allPageData: AllPageData = {
        ...COMMON_PAGE_DATA,
        ...ACCOUNTING_PAGE_DATA,
        ...VAT_PAGE_DATA,
        ...PERSONAL_PAGE_DATA,
        ...COPERATE_PAGE_DATA,
        ...SALARY_INSURANCE_PAGE_DATA,
    };

    // 현재 path에 맞는 페이지 타이틀 찾기
    const getCurrentPageTitle = (): string => {
        const currentPath = location.pathname;
        const pageEntry = Object.values(allPageData).find(page => page.path === currentPath);
        return pageEntry?.title || '';
    };

    const currentPageTitle = getCurrentPageTitle();

    // 초기값 설정
    useEffect(() => {
        if (periodData && !selectedPeriod) {
            setSelectedPeriod(periodData);
        }
    }, [periodData, selectedPeriod]);

    // 드롭다운이 열릴 때 활성화된 기수 요소로 스크롤
    useEffect(() => {
        if (isDropdownOpen && activeItemRef.current) {
            // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 스크롤 실행
            setTimeout(() => {
                activeItemRef.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'center',
                    inline: 'center'
                });
            }, 50);
        }
    }, [isDropdownOpen]);

    const handlePeriodClick = (): void => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handlePeriodSelect = async (period: PeriodData): Promise<void> => {
        if (!companyData?.companyId) {
            showToast(MESSAGES.COMPANY_NOT_FOUND, 'error');
            return;
        }

        try {
            // 1. API 호출로 기수 변경 요청
            const result = await changePeriod(companyData.companyId, period.period);

            if (result.success && result.data) {
                // 2. 성공 시 로컬 상태 즉시 업데이트 (UI 즉시 변경)
                setSelectedPeriod(period);

                // 3. URL 파라미터도 업데이트 (새로고침 시에도 유지)
                const newParams = new URLSearchParams(searchParams);
                newParams.set('period', period.period.toString());

                // 선택된 기간의 날짜 범위로 dateRange 업데이트
                const dateRange = `${period.startDate}${period.endDate}`.replace(/-/g, '');
                newParams.set('dateRange', dateRange);

                setSearchParams(newParams, { replace: true });

                // 4. 페이지 새로고침으로 변경사항 완전 적용
                window.location.reload();

            } else {
                // 실패 시 메시지 표시
                showToast(MESSAGES.PERIOD_CHANGE_FAILED, 'error');
            }
        } catch (error: unknown) {
            showToast(MESSAGES.PERIOD_CHANGE_FAILED, 'error');
        }

        setIsDropdownOpen(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePeriodClick();
        }
        if (event.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    const handlePeriodItemKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, period: PeriodData): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePeriodSelect(period);
        }
    };

    // 외부 클릭 시 드롭다운 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div className={styles.layoutHeader}>
            <div className={styles.layoutHeader__item}>
                <div className={styles.layoutHeader__itemTitle}>
                    <div className={styles.layoutHeader__companyInfo}>
                        {/* 페이지 타이틀을 원래 사업자명 자리에 배치 */}
                        {currentPageTitle && (
                            <h2 className={styles.layoutHeader__pageTitle}>{currentPageTitle}</h2>
                        )}
                        <div className={styles.layoutHeader__periodGroup}>
                            {/* 사업자명을 기수 왼쪽으로 이동 */}
                            <span className={styles.layoutHeader__companyName}>{companyData?.korName}</span>
                            <div className={styles.layoutHeader__periodDropdown} ref={dropdownRef}>
                                <button
                                    className={`${styles.layoutHeader__periodBadge} ${isDropdownOpen ? styles['layoutHeader__periodBadge--open'] : ''}`}
                                    onClick={handlePeriodClick}
                                    onKeyDown={handleKeyDown}
                                    aria-expanded={isDropdownOpen}
                                    aria-haspopup="listbox"
                                    aria-label={MESSAGES.PERIOD_SELECTION_LABEL}
                                    type="button"
                                >
                                    <span>{selectedPeriod?.period || periodData?.period}기</span>
                                    <span className={`${styles.layoutHeader__dropdownArrow} ${isDropdownOpen ? styles['layoutHeader__dropdownArrow--open'] : ''}`}>▼</span>
                                </button>
                                {isDropdownOpen && periodList && (
                                    <ul className={styles.layoutHeader__periodList} role="listbox">
                                        {periodList.map((period: PeriodData, index: number) => {
                                            const isActive = period.period === (selectedPeriod?.period || periodData?.period);
                                            return (
                                                <li
                                                    key={`period-${period.period}`}
                                                    ref={isActive ? activeItemRef : null}
                                                    className={`${styles.layoutHeader__periodItem} ${isActive ? styles['layoutHeader__periodItem--active'] : ''}`}
                                                    onClick={() => handlePeriodSelect(period)}
                                                    onKeyDown={(e) => handlePeriodItemKeyDown(e, period)}
                                                    role="option"
                                                    aria-selected={isActive}
                                                    tabIndex={0}
                                                >
                                                    <span>{period.period} 기</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                            <span className={styles.layoutHeader__dateRange}>
                                {selectedPeriod?.startDate || periodData?.startDate} ~ {selectedPeriod?.endDate || periodData?.endDate}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
